import React, { useEffect, useMemo, useState } from "react";
import { User } from "../../../../types/user";
import { fetchUser, fetchUserCase } from "../../../../services/httpClient";
import { useParams } from "react-router-dom";
import ToastMsg from "../../../ui/ToastMsg";
import PageTitle from "../../../ui/PageTitle";
import { Button, Card, Col, Container, Row, Spinner } from "react-bootstrap";
import HouseholdCard from "./HouseholdCard";
import EditGuardian from "./EditGuardian";
import EditMember from "./EditMember";
import SaveChangesModal from "../../../ui/SaveChangesModal";
import CreateMember from "./CreateMember";

interface HouseholdUsersState {
  myCase: boolean;
  userToSave: boolean;
  setUserToSave: any;
  showModal: boolean;
  setShowModal: any;
  getData: boolean;
  family: any;
  getFamily: any;
}

const HouseholdUsers = ({ myCase, userToSave, setUserToSave, showModal, setShowModal, getData, family, getFamily }: HouseholdUsersState) => {
  const { id } = useParams();
  const [currentUser, setCurrentUser] = useState<User>();
  const [currentCase, setCurrentCase] = useState<any>();
  const [status, setStatus] = useState<string>("");
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [create, setCreate] = useState<boolean>(false);
  const [editGuardian, setEditGuardian] = useState<boolean>(false);
  const [editChild, setEditChild] = useState<boolean>(false);
  const [editHousehold, setEditHousehold] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(true);

  const guardians = useMemo(() => family.filter((f: any) => f.is_guardian === 1 && !f.deleted_at), [family]);
  const members = useMemo(() => family.filter((f: any) => f.is_guardian !== 1 && !f.deleted_at), [family]);
  const primaryContact = useMemo(() => guardians.find((g: any) => g.is_primary), [guardians]);
  const children = useMemo(() => members.filter((m: any) => m.relationship === "child"), [members]);
  const household = useMemo(() => members.filter((m: any) => m.relationship !== "child"), [members]);

  useEffect(() => {
    if (getData) {
      setIsLoading(true);
      getCase();
    }
    setIsLoading(false);
  }, [getData]);

  //if no name for a CM replace with email
  useEffect(() => {
    if (currentCase?.primaryCM?.user_name === " ") {
      fetchUser(currentCase.primaryCM.user_id)
        .then((res: any) => {
          setCurrentCase((prev: any) => ({
            ...prev,
            primaryCM: {
              ...prev.primaryCM,
              user_name: res[0].data.email,
            },
          }));
        })
        .catch((err: any) => {
          console.log(err);
          setIsLoading(false);
        });
    }
    if (currentCase?.secondaryCM?.user_name === " ") {
      fetchUser(currentCase.secondaryCM.user_id)
        .then((res: any) => {
          setCurrentCase((prev: any) => ({
            ...prev,
            secondaryCM: {
              ...prev.secondaryCM,
              user_name: res[0].data.email,
            },
          }));
        })
        .catch((err: any) => {
          console.log(err);
          setIsLoading(false);
        });
    }
  }, [currentCase]);

  const getCase = () => {
    fetchUserCase(id)
      .then((res: any) => {
        setCurrentCase(res[0].data);
        setStatus(res[0].data.casestatus);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onLeave = () => {
    resetHouseholdTab();
  };

  const onClickDoneEdit = (forceNoModal: boolean = false) => {
    if (!forceNoModal && userToSave) {
      setShowModal(true);
    } else {
      getFamily();
      resetHouseholdTab();
    }
  };

  const showCreate = (e: any) => {
    if (userToSave) {
      setShowModal(true);
    } else {
      setCreate(true);
      setEditGuardian(false);
      setEditChild(false);
      setEditHousehold(false);
      setCurrentUser(undefined);
    }
  };

  const resetHouseholdTab = () => {
    setCreate(false);
    setEditGuardian(false);
    setEditChild(false);
    setEditHousehold(false);
    setCurrentUser(undefined);
    setUserToSave(false);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <>
          {create ? (
            <CreateMember
              fetchHousehold={getFamily}
              startingGuardians={guardians}
              startingHousehold={household}
              startingChildren={children}
              onClickDoneEdit={onClickDoneEdit}
              setUserToSave={setUserToSave}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
            />
          ) : editGuardian ? (
            <EditGuardian
              user={currentUser}
              fetchHousehold={getFamily}
              resetHouseholdTab={resetHouseholdTab}
              setUserToSave={setUserToSave}
              guardians={guardians}
              currentCase={currentCase}
              onClickDoneEdit={onClickDoneEdit}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              myCase={myCase}
              caseStatus={status}
              setIsLoading={setIsLoading}
            />
          ) : editHousehold ? (
            <EditMember
              user={currentUser}
              fetchHousehold={getFamily}
              resetHouseholdTab={resetHouseholdTab}
              type="Household Member"
              currentCase={currentCase}
              setUserToSave={setUserToSave}
              onClickDoneEdit={onClickDoneEdit}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              myCase={myCase}
              caseStatus={status}
              setIsLoading={setIsLoading}
            />
          ) : editChild ? (
            <EditMember
              user={currentUser}
              fetchHousehold={getFamily}
              resetHouseholdTab={resetHouseholdTab}
              type="Child"
              currentCase={currentCase}
              setUserToSave={setUserToSave}
              onClickDoneEdit={onClickDoneEdit}
              setShowToast={setShowToast}
              setToastText={setToastText}
              setToastBody={setToastBody}
              myCase={myCase}
              caseStatus={status}
              setIsLoading={setIsLoading}
            />
          ) : (
            <>
              {myCase ? (
                <PageTitle title="Household" buttonTitle="Add Member" onButtonClick={() => showCreate(true)} type="section" />
              ) : (
                <PageTitle title="Household" type="section" />
              )}
              <Row className="flex-wrap pb-3">
                <Col xs={8} sm={12}>
                  <Card className="w-100">
                    <Card.Body>
                      <Row>
                        <Col xs={8} sm={6} md={6} lg={3} xl={3}>
                          <Row className="mb-1">
                            <div className="px-3 py-2">
                              <h5 className="app-header-text">Address</h5>
                              {currentCase?.address_line_one ? (
                                <p className="pt-0 pb-0 mb-0">
                                  <small>
                                    {currentCase?.address_line_one && currentCase?.address_line_one}
                                    {currentCase?.address_line_one && <br />}
                                    {currentCase?.address_line_two && currentCase?.address_line_two}
                                    {currentCase?.address_line_two && <br />}
                                    {currentCase?.city &&
                                      currentCase?.state &&
                                      currentCase?.city + ", " + currentCase?.state + "  " + currentCase?.zip}
                                  </small>
                                </p>
                              ) : (
                                <small>No Address Saved</small>
                              )}
                            </div>
                          </Row>
                        </Col>
                        <Col xs={8} sm={6} md={6} lg={3} xl={3}>
                          <Row className="mb-1">
                            <div className="px-3 py-2">
                              <h5 className="app-header-text">Main Contact</h5>
                              {primaryContact?.firstname ? (
                                <>
                                  <p className="pt-0 pb-0 mb-0">
                                    <small>
                                      {primaryContact?.firstname}{" "}
                                      {primaryContact?.preferredname ? "'" + primaryContact?.preferredname + "'" : ""}{" "}
                                      {primaryContact?.lastname}
                                    </small>
                                  </p>
                                  <p className="pt-0 pb-0 mb-0">
                                    <small>{primaryContact?.email}</small>
                                  </p>
                                  <p className="pt-0 pb-0 mb-0">
                                    <small>
                                      {primaryContact?.phonenumber === "1"
                                        ? ""
                                        : primaryContact?.phonenumber
                                        ? primaryContact?.phonenumber.replace(/(\d{1})(\d{3})(\d{3})(\d{3})/, "+$1 ($2) $3-$4")
                                        : ""}
                                    </small>
                                  </p>
                                </>
                              ) : (
                                <small>No Primary Setup</small>
                              )}
                            </div>
                          </Row>
                        </Col>
                        <Col xs={8} sm={6} md={6} lg={3} xl={3}>
                          <Row className="mb-1">
                            <div className="px-3 py-2">
                              <h5 className="app-header-text">Support</h5>
                              {currentCase?.support?.length > 0 ? (
                                currentCase?.support?.map((c: any, index: number) => (
                                  <p key={index} className="pt-0 pb-0 mb-0">
                                    <small className="text-capitalize">{c?.user_name}</small>
                                  </p>
                                ))
                              ) : (
                                <small>No Support Member(s)</small>
                              )}
                            </div>
                          </Row>
                        </Col>
                        <Col xs={8} sm={6} md={6} lg={3} xl={3}>
                          <Row className="mb-1">
                            <div className="px-3 py-2">
                              <h5 className="app-header-text">Case Manager</h5>
                              {currentCase?.primaryCM ? (
                                <p className="pt-0 pb-0 mb-0">
                                  <small>{currentCase?.primaryCM?.user_name} - Primary</small>
                                </p>
                              ) : (
                                <small>No Primary Case Manager{"\n"}</small>
                              )}
                              {currentCase?.secondaryCM ? (
                                <p className="pt-0 pb-0 mb-0">
                                  <small>{currentCase?.secondaryCM?.user_name} - Secondary</small>
                                </p>
                              ) : (
                                <small>No Secondary Case Manager</small>
                              )}
                            </div>
                          </Row>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              {myCase && (guardians.length > 0 || household.length > 0) && <PageTitle title="Adults" type="section" />}
              <Row className="d-flex align-items-stretch flex-wrap">
                {guardians.map((u: User) => (
                  <Col xs={8} sm={8} md={6} lg={6} xl={4} key={u.id} className="pb-3 h-100">
                    <HouseholdCard
                      user={u}
                      setEditGuardian={setEditGuardian}
                      setEditChild={setEditChild}
                      setEditHousehold={setEditHousehold}
                      setCreate={setCreate}
                      currentUser={currentUser}
                      setCurrentUser={setCurrentUser}
                      userToSave={userToSave}
                      setShowModal={setShowModal}
                      setIsLoading={setIsLoading}
                    />
                  </Col>
                ))}
                {household.map((u: User) => (
                  <Col xs={8} sm={8} md={6} lg={6} xl={4} key={u.id} className="pb-3 h-100">
                    <HouseholdCard
                      user={u}
                      setEditGuardian={setEditGuardian}
                      setEditChild={setEditChild}
                      setEditHousehold={setEditHousehold}
                      setCreate={setCreate}
                      currentUser={currentUser}
                      setCurrentUser={setCurrentUser}
                      userToSave={userToSave}
                      setShowModal={setShowModal}
                      setIsLoading={setIsLoading}
                    />
                  </Col>
                ))}
              </Row>
              {children.length > 0 && <PageTitle title="Children" type="section" />}
              <Row className="flex-wrap align-items-start align-items-stretch">
                {children.map((u: User) => (
                  <Col xs={8} sm={8} md={6} lg={6} xl={4} key={u.id} className="pb-3 h-100">
                    <HouseholdCard
                      user={u}
                      setEditGuardian={setEditGuardian}
                      setEditChild={setEditChild}
                      setEditHousehold={setEditHousehold}
                      setCreate={setCreate}
                      currentUser={currentUser}
                      setCurrentUser={setCurrentUser}
                      userToSave={userToSave}
                      setShowModal={setShowModal}
                      setIsLoading={setIsLoading}
                    />
                  </Col>
                ))}
              </Row>
            </>
          )}
          <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={() => onLeave()} />
        </>
      )}
    </>
  );
};

export default HouseholdUsers;
